import { useEffect, useMemo, useState } from 'react';
import { pick, isEqual, differenceWith } from 'lodash';
import { useProductsList } from 'api/products';
import { Product, ProductCategory } from 'api/types/product';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  getLocalProductsByStore,
  saveLocalProducts,
  saveNewLocalProducts,
  removeNewLocalProductsFromStore,
  getNewLocalProductsByStore,
  ProductLocalStatus,
} from 'utils/product';
import { usePrevious } from './usePrevious';
import { Role } from 'api/types/auth';
import { useSignedUser } from 'api/auth';

interface ProductsParams {
  storeId: number;
  hasClosedTutorial: boolean;
  isActive?: boolean;
}

interface ProductsValues {
  isLoading: boolean;
  products: Product[];
  notifyMe: boolean;
  newLocalProducts: Record<string, ProductLocalStatus>;
  mutate: () => void;
}

interface ProductsHook {
  (params: ProductsParams): ProductsValues;
}

const CATEGORIES: { [key: string]: ProductCategory[] } = {
  'local-products': [
    ProductCategory.LP,
    ProductCategory.POLP,
    ProductCategory.AF,
    ProductCategory.LS,
  ],
  preorder: [ProductCategory.POLP, ProductCategory.LP],
  'in-home': [ProductCategory.IH, ProductCategory.PO, ProductCategory.LINK],
  default: Object.values(ProductCategory),
};

const useProducts: ProductsHook = ({ storeId, hasClosedTutorial, isActive = false }) => {
  const { data: user } = useSignedUser();
  const history = useHistory();
  const location = useLocation();

  const [notifyMe, setNotifyMe] = useState(false);
  const { category = 'all' } = useParams<{ category?: string }>();
  const productCategories: ProductCategory[] = CATEGORIES[category] ?? CATEGORIES.default;
  const prevProductCategories = usePrevious<ProductCategory[]>(productCategories);
  const newLocalProducts = getNewLocalProductsByStore(user?.owner_id, storeId);

  const { data, isLoading, mutate } = useProductsList({
    storeId,
    sort: 'store_arrangement',
    isActive,
  });

  const products = useMemo(() => data ?? [], [data]);

  const getProductsByCategory = (productCategories: ProductCategory[]): Product[] => {
    return products.filter((product) => productCategories.includes(product.category));
  };

  const savingNewLocalProducts = (
    localProducts: Partial<Product>[],
    callback: (localProducts: Record<string, ProductLocalStatus>) => void,
  ) => {
    const prevLocalProducts = getLocalProductsByStore(user.owner_id, storeId);
    const diff = differenceWith(localProducts, prevLocalProducts, isEqual);
    const prevLocalproductIds = prevLocalProducts.map(({ id }) => id);

    const newLocalProducts = diff.reduce(
      (res, { id }) => ({
        ...res,
        [id]: prevLocalproductIds.includes(id)
          ? ProductLocalStatus.updated
          : ProductLocalStatus.new,
      }),
      {},
    );
    callback(newLocalProducts);
  };

  const localProducts = useMemo(
    () =>
      getProductsByCategory(CATEGORIES['local-products']).map((item) =>
        pick(item, ['id', 'name', 'description', 'price']),
      ),
    [JSON.stringify(products)],
  );

  const productsByCategory = useMemo(
    () => getProductsByCategory(productCategories),
    [JSON.stringify(products), productCategories],
  );

  useEffect(() => {
    if (!isLoading) {
      savingNewLocalProducts(localProducts, (newLocalProducts) => {
        if (Object.keys(newLocalProducts).length) {
          setNotifyMe(true);
          saveNewLocalProducts(newLocalProducts, storeId, user.owner_id);
          return;
        }
        setNotifyMe(false);
      });
    }
  }, [JSON.stringify(localProducts), isLoading, user]);

  useEffect(() => {
    if (productCategories === CATEGORIES['local-products'] && notifyMe) {
      saveLocalProducts(localProducts, storeId, user.owner_id);
      setNotifyMe(false);
    }
  }, [productCategories, notifyMe, user]);

  useEffect(() => {
    if (productCategories !== CATEGORIES['local-products'] && prevProductCategories) {
      removeNewLocalProductsFromStore(user.owner_id, storeId);
    }
  }, [productCategories, user]);

  useEffect(() => {
    if (!isLoading && !data?.length && !hasClosedTutorial && user?.role === Role.HOST) {
      history.push({
        pathname: '/onboarding',
        state: { background: location, products: products.length, storeId, stores: 0 },
      });
    }
  }, [isLoading, user, hasClosedTutorial, history, location, products.length, storeId]);

  return { isLoading, products: productsByCategory, notifyMe, newLocalProducts, mutate };
};

export default useProducts;
